import React, { useState, useEffect } from 'react';
import { TrophyIcon, ArrowDownTrayIcon, ShareIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', navy:'#0D1B2A', gold:'#C9A84C', success:'#10B981', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };
const LEVEL_COLORS = { A1:'#6B7280', A2:'#3B82F6', B1:'#00B4D8', B2:'#10B981', C1:'#F59E0B', C2:'#EAB308' };
const LEVEL_NAMES = { A1:'Beginner', A2:'Elementary', B1:'Intermediate', B2:'Upper Intermediate', C1:'Advanced', C2:'Mastery' };

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/api/student/certificates')
      .then(r => setCerts(r.data?.certificates || []))
      .catch(() => setCerts([]))
      .finally(() => setLoading(false));
  }, []);

  const getCode = cert => cert.verify_code || cert.verification_url?.split('/').pop() || '';

  const shareLinkedIn = (cert) => {
    const code = getCode(cert);
    const lvlName = LEVEL_NAMES[cert.level] || cert.level;
    const school = cert.school_name || 'SpeakIQ';
    const text = encodeURIComponent(
      `I just achieved ${cert.level} — ${lvlName} English certification through ${school}!\n` +
      `Verify my achievement: https://speakiq.uk/verify/${code}\n` +
      `#EnglishLearning #CEFR #SpeakIQ #${school.replace(/\s+/g,'')}`
    );
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://speakiq.uk/verify/${code}`)}&summary=${text}`, '_blank');
  };

  const shareTelegram = (cert) => {
    const code = getCode(cert);
    const lvlName = LEVEL_NAMES[cert.level] || cert.level;
    const text = encodeURIComponent(
      `I achieved ${cert.level} — ${lvlName} English certification! 🎓\nVerify: https://speakiq.uk/verify/${code}`
    );
    window.open(`https://t.me/share/url?url=${encodeURIComponent(`https://speakiq.uk/verify/${code}`)}&text=${text}`, '_blank');
  };

  const requestPhysical = async (cert) => {
    const code = getCode(cert);
    if (!window.confirm("Request a physical copy for $15? We'll email you with payment details within 24 hours.")) return;
    setRequesting(r => ({...r, [code]: true}));
    try {
      await api.post(`/api/student/certificates/${code}/request-physical`);
      toast.success("Request sent! We'll contact you within 24 hours.");
    } catch { toast.error('Failed to send request. Please email hello@speakiq.uk'); }
    finally { setRequesting(r => ({...r, [code]: false})); }
  };

  return (
    <div style={{ background:C.bg, minHeight:'100vh' }}>
      <header style={{ background:C.navy, color:'#fff', padding:'0 32px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ fontWeight:800, fontSize:20 }}>SpeakIQ</div>
        <nav style={{ display:'flex', gap:24 }}>
          {[['Dashboard','/dashboard'],['Vocabulary','/vocabulary'],['Certificates','/my-certificates']].map(([l,p]) => (
            <button key={p} onClick={() => navigate(p)} style={{ background:'none', border:'none', color: p==='/my-certificates'?'#fff':'rgba(255,255,255,0.7)', fontSize:14, fontWeight: p==='/my-certificates'?700:500, cursor:'pointer' }}>{l}</button>
          ))}
        </nav>
      </header>

      <div style={{ maxWidth:1000, margin:'0 auto', padding:'32px' }}>
        <h1 style={{ margin:'0 0 8px', fontSize:28, fontWeight:800 }}>My Certificates</h1>
        <p style={{ margin:'0 0 32px', color:C.grey }}>Your earned English proficiency certificates — download, share, or request a physical copy</p>

        {loading ? (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20 }}>
            {[1,2].map(i => <div key={i} className="skeleton" style={{ height:340, borderRadius:16 }} />)}
          </div>
        ) : certs.length === 0 ? (
          <div className="card" style={{ padding:64, textAlign:'center' }}>
            <TrophyIcon style={{ width:56, height:56, margin:'0 auto 16px', color:'#D1D5DB' }} />
            <h2 style={{ margin:'0 0 8px', fontSize:20, fontWeight:700 }}>No certificates yet</h2>
            <p style={{ margin:'0 0 24px', color:C.grey }}>Complete your level exam to earn your first certificate!</p>
            <button onClick={() => navigate('/level-exam')} className="btn-teal" style={{ padding:'12px 32px' }}>
              Take Level Exam →
            </button>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(440px,1fr))', gap:24 }}>
            {certs.map((cert, i) => {
              const color = LEVEL_COLORS[cert.level] || C.teal;
              const code = getCode(cert);
              const lvlName = LEVEL_NAMES[cert.level] || '';
              return (
                <div key={i} className="card" style={{ overflow:'hidden', borderRadius:16 }}>
                  {/* Certificate card header */}
                  <div style={{ background:`linear-gradient(135deg, ${C.navy} 0%, #1a2f4a 100%)`, padding:'28px 28px 20px', color:'#fff', position:'relative', overflow:'hidden' }}>
                    <div style={{ position:'absolute', top:-30, right:-30, width:120, height:120, borderRadius:'50%', background:`${color}20` }} />
                    <div style={{ position:'absolute', bottom:-40, left:20, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.04)' }} />
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', position:'relative' }}>
                      <div>
                        <div style={{ fontSize:11, opacity:0.5, marginBottom:8, letterSpacing:'0.15em', textTransform:'uppercase' }}>Certificate of Proficiency</div>
                        <div style={{ display:'flex', alignItems:'baseline', gap:10 }}>
                          <div style={{ fontSize:44, fontWeight:900, color, lineHeight:1 }}>{cert.level}</div>
                          <div style={{ fontSize:15, opacity:0.75, fontStyle:'italic' }}>{lvlName}</div>
                        </div>
                        <div style={{ fontSize:12, opacity:0.45, marginTop:12 }}>
                          Issued {cert.issued_at ? new Date(cert.issued_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }) : 'Recently'}
                        </div>
                      </div>
                      <TrophyIcon style={{ width:44, height:44, color, opacity:0.7, flexShrink:0 }} />
                    </div>
                    {/* Verification code */}
                    <div style={{ marginTop:16, display:'flex', alignItems:'center', gap:8, background:'rgba(255,255,255,0.07)', borderRadius:8, padding:'8px 12px' }}>
                      <span style={{ fontSize:10, opacity:0.5, textTransform:'uppercase', letterSpacing:1 }}>Code:</span>
                      <span style={{ fontFamily:'monospace', fontSize:13, fontWeight:700, letterSpacing:2, color }}>{code}</span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:8 }}>
                    {/* Download */}
                    <a href={`/api/certificates/${code}/download`} target="_blank" rel="noopener noreferrer"
                      style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'11px', background:C.gold, color:'#fff', borderRadius:10, fontWeight:700, fontSize:14, textDecoration:'none' }}>
                      <ArrowDownTrayIcon style={{ width:16, height:16 }} /> Download PDF Certificate
                    </a>

                    {/* Share row */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:8 }}>
                      <button onClick={() => shareLinkedIn(cert)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px', background:'#0A66C2', color:'#fff', border:'none', borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                        LinkedIn
                      </button>
                      <button onClick={() => shareTelegram(cert)} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px', background:'#2AABEE', color:'#fff', border:'none', borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                        Telegram
                      </button>
                    </div>

                    {/* Copy link */}
                    <button onClick={() => { navigator.clipboard.writeText(`https://speakiq.uk/verify/${code}`); toast.success('Link copied!'); }}
                      style={{ padding:'9px', background:'#F3F4F6', color:C.dark, border:`1px solid ${C.border}`, borderRadius:10, fontWeight:600, fontSize:13, cursor:'pointer' }}>
                      Copy Verification Link
                    </button>

                    {/* Physical copy */}
                    <button onClick={() => requestPhysical(cert)} disabled={requesting[code]}
                      style={{ padding:'9px', background:'#fff', color:C.grey, border:`1px solid ${C.border}`, borderRadius:10, fontWeight:500, fontSize:12, cursor:'pointer' }}>
                      {requesting[code] ? 'Sending request...' : 'Request Physical Copy ($15) — ships in 5-7 days'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
